<template>
  <section :class="['box', containerClass]">
    <h3>{{ title }}</h3>

    <div v-if="winners.length === 0">
      <p class="no_win">{{ $_translations.value.page_winners_public_section_no_winner_yet }}</p>
      <p class="next_win">{{ $_translations.value.page_winners_public_section_next_winner_announced }} <b>{{ nextDrawDate }}</b></p>
    </div>

    <div v-else>
      <WinnersPublicUserItem
        v-for="winner in visibleWinners"
        :key="winner.id || winner.username"
        :winner="winner"
      />

      <a
        v-if="hasMoreWinners"
        href="#"
        class="load-more"
        @click.prevent="$emit('toggle-expansion', prizeType)"
      >
        <i :class="isExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
      </a>

      <p class="next_win">{{ $_translations.value.page_winners_public_section_next_winner_announced }} <b>{{ nextDrawDate }}</b></p>
    </div>

    <p class="last_upd">{{ $_translations.value.page_winners_public_section_last_updated }} <b>{{ formatShortDate(lastUpdated) }}</b></p>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';
import WinnersPublicUserItem from '../item/WinnersPublicUserItem.vue';
import { prizeTypeEnum } from '@/constants/winners.js';

const props = defineProps({
  prizeType: {
    type: String,
    required: true,
    validator: (value) => Object.values(prizeTypeEnum).includes(value),
  },
  winners: {
    type: Array,
    default: () => [],
  },
  lastUpdated: {
    type: String,
    default: '',
  },
  isExpanded: {
    type: Boolean,
    default: false,
  },
  maxInitialWinners: {
    type: Number,
    default: 2,
  },
});

defineEmits(['toggle-expansion']);
const { proxy } = getCurrentInstance();
// Computed properties
const title = computed(() => {
  const titles = {
    [prizeTypeEnum.GRAND]: proxy.$_translations.value.page_winners_public_section_grand_prize_title,
    [prizeTypeEnum.MONTHLY]: proxy.$_translations.value.page_winners_public_section_monthly_prize_title,
    [prizeTypeEnum.WEEKLY]: proxy.$_translations.value.page_winners_public_section_weekly_prize_title,
    [prizeTypeEnum.DAILY]: proxy.$_translations.value.page_winners_public_section_daily_prize_title,
  };
  return titles[props.prizeType] || proxy.$_translations.value.page_winners_public_section_default_prize_title;
});

const containerClass = computed(() => {
  const classes = {
    [prizeTypeEnum.GRAND]: 'grand_prize',
    [prizeTypeEnum.MONTHLY]: 'grand_prize',
    [prizeTypeEnum.WEEKLY]: 'weekly_prize',
    [prizeTypeEnum.DAILY]: '',
  };
  return classes[props.prizeType] || '';
});

const visibleWinners = computed(() => {
  return props.isExpanded
    ? props.winners
    : props.winners.slice(0, props.maxInitialWinners);
});

const hasMoreWinners = computed(() => {
  return props.winners.length > props.maxInitialWinners;
});

const nextDrawDate = computed(() => {
  const today = new Date();
  let nextDate = new Date(today);

  switch (props.prizeType) {
    case prizeTypeEnum.WEEKLY:
      nextDate.setDate(today.getDate() + 7);
      break;
    case prizeTypeEnum.MONTHLY:
      nextDate.setMonth(today.getMonth() + 1);
      break;
    case prizeTypeEnum.GRAND:
      nextDate.setFullYear(today.getFullYear() + 1);
      break;
    case prizeTypeEnum.DAILY:
      nextDate.setDate(today.getDate() + 1);
      break;
    default:
      nextDate.setDate(today.getDate() + 30);
  }

  return formatShortDate(nextDate.toISOString());
});

// Format date for display (short format DD/MM/YYYY)
const formatShortDate = (dateString) => {
  if (!dateString) return 'DD/MM/YYYY';

  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return 'DD/MM/YYYY';
  }
};
</script>
